import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useCurrency } from '../../context/CurrencyContext';

interface BudgetBarProps {
  expected: number;
  paid: number;
  actual?: number;
  compact?: boolean;
}

const BudgetBar: React.FC<BudgetBarProps> = ({ expected, paid, actual, compact }) => {
  const { symbol } = useCurrency();
  // If actual exists, it replaces expected as the total amount
  const total = actual != null && actual > 0 ? actual : expected;
  if (!total) return null;

  const paidPercent = Math.min((paid / total) * 100, 100);
  const remaining = total - paid;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant={compact ? 'caption' : 'body2'} sx={{ fontWeight: 600 }}>
          {symbol}{paid.toLocaleString()} paid
        </Typography>
        <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary">
          {symbol}{remaining.toLocaleString()} remaining
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={paidPercent}
        sx={{
          height: compact ? 6 : 10,
          borderRadius: compact ? 3 : 5,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            borderRadius: compact ? 3 : 5,
            bgcolor: paidPercent >= 100 ? 'success.main' : 'primary.main',
          },
        }}
      />
      {!compact && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          {actual != null && actual > 0 ? (
            <Typography variant="body2" color="text.secondary">
              Total: {symbol}{actual.toLocaleString()}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Expected: {symbol}{expected.toLocaleString()}
            </Typography>
          )}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {Math.round(paidPercent)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BudgetBar;
